import { api } from './client'
import type {
  AdminSettings,
  AdminUser,
  AuthConfig,
  CheckResult,
  CurrentUser,
  FeedPost,
  PublicConfig,
  Room,
  RoomMember,
  RoomMessage,
} from './types'

export const authApi = {
  config: () => api.get<AuthConfig>('/auth/config'),
  me: () => api.get<{ user: CurrentUser | null }>('/auth/me'),
  login: (email: string, password: string) =>
    api.post<{ token: string; user: CurrentUser }>('/auth/login', { email, password }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
}

export const setupApi = {
  status: () => api.get<{ completed: boolean }>('/setup/status'),
  complete: (body: unknown) => api.post<{ ok: true }>('/setup/complete', body),
}

export const publicApi = {
  config: () => api.get<PublicConfig>('/config/public'),
}

export const roomsApi = {
  list: () => api.get<{ rooms: Room[] }>('/rooms'),
  get: (slug: string) => api.get<{ room: Room }>(`/rooms/${encodeURIComponent(slug)}`),
  create: (data: {
    name: string
    isPrivate: boolean
    password?: string
    lobbyEnabled?: boolean
    chatEnabled?: boolean
    screenShareEnabled?: boolean
    recordingEnabled?: boolean
  }) => api.post<{ room: Room }>('/rooms', data),
  join: (slug: string, password?: string) =>
    api.post<{ room: Room; ticket: string }>(`/rooms/${encodeURIComponent(slug)}/join`, {
      password: password ?? '',
    }),
  update: (id: string, data: Partial<Room> & { password?: string }) =>
    api.patch<{ room: Room }>(`/rooms/${encodeURIComponent(id)}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/rooms/${encodeURIComponent(id)}`),
  messages: (slug: string, limit = 200) =>
    api.get<{ messages: RoomMessage[] }>(`/rooms/${encodeURIComponent(slug)}/messages?limit=${limit}`),
  clearMessages: (id: string) =>
    api.delete<{ ok: true; deleted: number }>(`/rooms/${encodeURIComponent(id)}/messages`),
  members: (id: string) =>
    api.get<{ members: RoomMember[] }>(`/rooms/${encodeURIComponent(id)}/members`),
  removeMember: (id: string, userId: string) =>
    api.delete<{ ok: true; kicked: number }>(
      `/rooms/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
    ),
}

export const adminApi = {
  listUsers: () => api.get<{ users: AdminUser[] }>('/admin/users'),
  createUser: (data: {
    email: string
    displayName: string
    password?: string
    role: 'ADMIN' | 'USER'
  }) => api.post<{ user: AdminUser }>('/admin/users', data),
  updateUser: (
    id: string,
    data: { displayName?: string; role?: 'ADMIN' | 'USER'; disabled?: boolean; password?: string },
  ) => api.patch<{ user: AdminUser }>(`/admin/users/${encodeURIComponent(id)}`, data),
  deleteUser: (id: string) => api.delete<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}`),
  getSettings: () => api.get<AdminSettings>('/admin/settings'),
  updateSettings: (data: unknown) => api.put<{ ok: true }>('/admin/settings', data),
  checkOidc: (issuer: string) =>
    api.post<{ results: CheckResult[] }>('/admin/settings/check-oidc', { issuer }),
  checkTurn: (stunUrls: string, turnUrl: string) =>
    api.post<{ results: CheckResult[] }>('/admin/settings/check-turn', { stunUrls, turnUrl }),
}

export const feedApi = {
  list: () => api.get<{ posts: FeedPost[] }>('/feed'),
  create: (text: string) => api.post<{ post: FeedPost }>('/feed', { text }),
  remove: (id: string) => api.delete<{ ok: true }>(`/feed/${encodeURIComponent(id)}`),
}
